const LANDING_EMBED_RESIZE_EVENT = 'legalprime:landing-embed-resize'
export const LANDING_EMBED_NAVIGATE_EVENT = 'legalprime:landing-embed-navigate'
const LANDING_EMBED_HTML_CLASS = 'legalprime-landing-embed'

export function isSafeLandingEmbedEscapePath(path: string): boolean {
  if (!path.startsWith('/campanha/') || path.includes('://') || path.startsWith('//')) {
    return false
  }

  const pathname = path.split(/[?#]/)[0] ?? ''
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length < 3 || segments[0] !== 'campanha') {
    return false
  }

  const step = segments[2]?.toLowerCase()
  return step === 'conta' || step === 'perfil' || step === 'checkout' || step === 'obrigado'
}

/**
 * Redireciona a janela principal (site pai) para uma rota interna da landing,
 * saindo do iframe do embed externo. Retorna true quando a navegação foi delegada ao top.
 */
export function escapeLandingEmbedToAppPath(relativePath: string): boolean {
  if (typeof window === 'undefined' || window.parent === window) {
    return false
  }

  const path = relativePath.trim()
  if (!isSafeLandingEmbedEscapePath(path)) {
    return false
  }

  const targetUrl = `${window.location.origin.replace(/\/+$/, '')}${path}`

  try {
    const link = document.createElement('a')
    link.href = targetUrl
    link.target = '_top'
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    link.remove()
  } catch {
    // Alguns browsers bloqueiam assign direto no top em iframe cross-origin.
  }

  try {
    window.parent.postMessage(
      {
        type: LANDING_EMBED_NAVIGATE_EVENT,
        url: targetUrl,
      },
      '*'
    )
  } catch {
    return false
  }

  return true
}

/**
 * Altura real do conteúdo da landing (rodapé incluso), sem forçar 100vh.
 */
export function measureLandingEmbedHeight(): number {
  const footer = document.getElementById('landing-footer')
  if (footer) {
    const rect = footer.getBoundingClientRect()
    const bottom = rect.bottom + window.scrollY
    return Math.ceil(bottom)
  }

  const docHeight = document.documentElement.scrollHeight
  const bodyHeight = document.body.scrollHeight
  return Math.ceil(Math.max(docHeight, bodyHeight))
}

export function postLandingEmbedResize(slug: string, campaignId?: number): void {
  if (!slug || typeof window === 'undefined' || window.parent === window) {
    return
  }

  window.parent.postMessage(
    {
      type: LANDING_EMBED_RESIZE_EVENT,
      slug,
      campaign_id: campaignId != null && campaignId > 0 ? campaignId : null,
      height: measureLandingEmbedHeight(),
    },
    '*',
  )
}

export function enableLandingEmbedDocumentMode(): void {
  document.documentElement.classList.add(LANDING_EMBED_HTML_CLASS)
}

export function disableLandingEmbedDocumentMode(): void {
  document.documentElement.classList.remove(LANDING_EMBED_HTML_CLASS)
}

export function createLandingEmbedResizeReporter(slug: string, campaignId?: number): {
  schedule: () => void
  dispose: () => void
} {
  let frameId: number | null = null

  const schedule = (): void => {
    if (frameId !== null) {
      cancelAnimationFrame(frameId)
    }
    frameId = requestAnimationFrame(() => {
      frameId = null
      postLandingEmbedResize(slug, campaignId)
    })
  }

  const onWidgetReady = (): void => {
    schedule()
  }

  enableLandingEmbedDocumentMode()
  schedule()

  const resizeObserver = new ResizeObserver(schedule)
  resizeObserver.observe(document.documentElement)

  window.addEventListener('load', schedule)
  window.addEventListener('resize', schedule)
  document.addEventListener('legalprime:widget-ready', onWidgetReady)

  const imageLoadInterval = window.setInterval(() => {
    const pendingImages = document.querySelectorAll('img:not([data-lp-embed-sized])')
    pendingImages.forEach((node) => {
      const img = node as HTMLImageElement
      if (img.complete) {
        img.setAttribute('data-lp-embed-sized', '1')
        return
      }
      img.addEventListener(
        'load',
        () => {
          img.setAttribute('data-lp-embed-sized', '1')
          schedule()
        },
        { once: true },
      )
      img.addEventListener(
        'error',
        () => {
          img.setAttribute('data-lp-embed-sized', '1')
          schedule()
        },
        { once: true },
      )
    })
    if (pendingImages.length === 0) {
      window.clearInterval(imageLoadInterval)
    }
  }, 400)

  window.setTimeout(schedule, 800)
  window.setTimeout(schedule, 2000)

  const dispose = (): void => {
    if (frameId !== null) {
      cancelAnimationFrame(frameId)
    }
    resizeObserver.disconnect()
    window.removeEventListener('load', schedule)
    window.removeEventListener('resize', schedule)
    document.removeEventListener('legalprime:widget-ready', onWidgetReady)
    window.clearInterval(imageLoadInterval)
    disableLandingEmbedDocumentMode()
  }

  return { schedule, dispose }
}
